import { useCallback, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import {
  ThemeContext,
  type EffectiveTheme,
  type ThemeContextValue,
  type ThemePreference,
} from "@/theme/themeContext";

const STORAGE_KEY = "eh-theme";

function loadInitial(): ThemePreference {
  if (typeof window === "undefined") return "system";
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored === "light" || stored === "dark") return stored;
  } catch {
    // ignore (private mode etc.)
  }
  return "system";
}

function resolveEffective(pref: ThemePreference): EffectiveTheme {
  if (pref !== "system") return pref;
  if (typeof window === "undefined") return "light";
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

function applyToRoot(effective: EffectiveTheme) {
  const root = document.documentElement;
  root.dataset.theme = effective;
  root.classList.toggle("dark", effective === "dark");
  root.style.colorScheme = effective;
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [preference, setPreferenceState] = useState<ThemePreference>(loadInitial);
  const [effective, setEffective] = useState<EffectiveTheme>(() => resolveEffective(loadInitial()));

  useEffect(() => {
    applyToRoot(effective);
  }, [effective]);

  // Bei "system" auf Wechsel der OS-Einstellung reagieren
  useEffect(() => {
    if (preference !== "system") return;
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const handle = () => setEffective(mq.matches ? "dark" : "light");
    mq.addEventListener("change", handle);
    return () => mq.removeEventListener("change", handle);
  }, [preference]);

  const setPreference = useCallback((next: ThemePreference) => {
    setPreferenceState(next);
    try {
      if (next === "system") window.localStorage.removeItem(STORAGE_KEY);
      else window.localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // ignore
    }
    setEffective(resolveEffective(next));
  }, []);

  const cyclePreference = useCallback(() => {
    setPreference(preference === "light" ? "dark" : preference === "dark" ? "system" : "light");
  }, [preference, setPreference]);

  const value = useMemo<ThemeContextValue>(
    () => ({ preference, effective, setPreference, cyclePreference }),
    [preference, effective, setPreference, cyclePreference],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}
